<script setup lang="ts">
import type { CmsElementPluebelCmsElementsDiyguide } from "~/composables/useCustom";
import type { Schemas } from "#shopware";
import { getCmsTranslate } from "@shopware/helpers";
import { defu } from "defu";
import type { operations } from "#shopware";

const { getErrorsCodes } = useCartNotification();
const { pushSuccess, pushError } = useNotifications();
const { resolveCartError } = useCartErrorParamsResolver();
const { addProducts, cart, refreshCart, changeProductQuantity } = useCart();
const { t } = useI18n();
const props = defineProps<{
  content: CmsElementPluebelCmsElementsDiyguide;
}>();

const { config } = props.content;
const {
  guide_headline,
  guide_main_image,
  guide_product_section,
  diy_box,
  guide_upload,
  product_project,
  how_difficult,
  timeinfo,
  guid_download_text,
  icon_implementation,
  icon_time,
} = config;
const paymentMethods =
  inject<ComputedRef<Schemas["PaymentMethod"][]>>("paymentMethods");

// Properly typed interface for selected products
interface SelectedProduct {
  id: string;
  quantity: number;
  referencedId: string; // Required for Shopware cart
  type: string; // Required for Shopware cart
  stackable: boolean; // Required for Shopware cart
  removable: boolean; // Required for Shopware cart
}

const selectedProducts = ref<SelectedProduct[]>([]);

// Calculate total price - Now referencing diyProducts after initialization
const totalPrice = computed(() => {
  return product_project?.value.reduce((total, item) => {
    if (!item.project_product_link) {
      return total;
    }
    const isSelected = selectedProducts?.value.find(
      (p) => p.referencedId === item.project_product_link.id
    );
    if (isSelected) {
      const productPrice =
        item.project_product_link.calculatedPrice?.unitPrice || 0;
      return total + productPrice * isSelected.quantity;
    }
    return total;
  }, 0);
});

// Updated select item function with proper typing
const selectItem = (product: any) => {
  const existingProduct = selectedProducts?.value.find(
    (p) => p.referencedId === product.id
  );

  if (existingProduct) {
    selectedProducts.value = selectedProducts?.value.filter(
      (p) => p.referencedId !== product.id
    );
    // Update quantity if product exists
    existingProduct.quantity = product.selectedQuantity || 1;
  } else {
    // Add new product with quantity
    const newProduct: SelectedProduct = {
      id: crypto.randomUUID(),
      referencedId: product.id,
      quantity: product.selectedQuantity || 1,
      type: "product",
      stackable: true,
      removable: true,
    };
    selectedProducts.value.push(newProduct);
  }
};

const updateQuantity = (product: any, quantity: number) => {
  // Ensure quantity is within bounds
  console.log("products", product);
  const validQuantity = Math.max(
    product.minPurchase || 1,
    Math.min(quantity, product.calculatedMaxPurchase || Infinity)
  );

  product.selectedQuantity = validQuantity;

  // Update selected products if already selected
  const existingProduct = selectedProducts?.value.find(
    (p) => p.referencedId === product.id
  );
  if (existingProduct) {
    existingProduct.quantity = validQuantity;
  }
};

// Check if a product is selected
const isProductSelected = (productId: string) => {
  return selectedProducts?.value.some((p) => p.referencedId === productId);
};

// Updated translations with proper typing
interface Translations {
  product: {
    addedToCart: string;
    qty: string;
    addToCart: string;
  };
  errors: {
    [key: string]: string;
  };
}

let translations: Translations = {
  product: {
    addedToCart: "has been added to cart.",
    qty: "Qty",
    addToCart: "Add to cart",
  },
  errors: {
    "product-stock-reached":
      "The product {name} is only available {quantity} times",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const isAddToCartProcessing = ref(false);

// Updated add to cart function with proper error handling
const addToCart = async () => {
  if (selectedProducts?.value.length === 0) return;

  isAddToCartProcessing.value = true;

  try {
    const cartItems = cart.value?.lineItems || [];

    for (const product of selectedProducts?.value) {
      const existingCartItem = cartItems.find(
        (item) => item.referencedId === product.referencedId
      );
      if (existingCartItem) {
        // Update quantity if the product already exists in the cart
        await changeProductQuantity({
          id: existingCartItem.id, // Line item ID in cart
          quantity: existingCartItem.quantity + product.quantity,
        });
      } else {
        // Add as a new product if not found in the cart
        await addProducts([product]);
      }
    }

    await refreshCart(); // Refresh the cart to get updated data

    pushSuccess(guide_headline.value + t("product.messages.addedToCart"));

    selectedProducts.value = []; // Clear selection only on success
  } catch (error) {
    pushError(t("plubel.detail.addToCartError"));
  } finally {
    isAddToCartProcessing.value = false;
  }
};

const handlePrint = () => {
  window.print();
};

const showShareModal = ref(false);

// Function to share via Web Share API
const shareViaAPI = async () => {
  const shareData = {
    title: document.title,
    url: window.location.href,
    text: t("plubel.detail.sharContent"),
  };

  try {
    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      showShareModal.value = true;
    }
  } catch (error) {
    console.error("Error sharing:", error);
    showShareModal.value = true;
  }
};

// Move watchEffect inside onMounted or after diyProducts is defined
const setupDefaultQuantities = () => {
  if (product_project?.value.length > 0) {
    product_project?.value.forEach((product) => {
      if (product.project_product_link) {
        // Set default quantity to 1 if not already set
        product.project_product_link.selectedQuantity =
          product.project_product_link.selectedQuantity || 1;
      }
    });
  }
};

const getCartQuantity = (productId: string) => {
  // This will reactively update whenever cart.value changes
  const cartItems = cart?.value?.lineItems || [];
  const existingCartItem = cartItems.find(
    (item) => item.referencedId === productId
  );
  return existingCartItem?.quantity || 0;
};

const checkCartEnabled = (product: any) => {
  if (!product) return true;

  const currentCartQuantity = getCartQuantity(product.id);
  // Get requested quantity (either selected quantity or minimum purchase amount)
  const requestedQuantity = product.selectedQuantity || 1;

  // Calculate total quantity (cart + requested)
  const totalQuantity = currentCartQuantity + requestedQuantity;

  return (
    !product.available ||
    product.stock < product.minPurchase || // Stock less than minimum purchase
    product.stock < totalQuantity || // Not enough stock for cart + requested quantity
    (product.calculatedMaxPurchase &&
      totalQuantity > product.calculatedMaxPurchase) // Would exceed max purchase limit
  );
};

const sliderConfig = computed(() => ({
  minHeight: {
    value: "300px",
    source: "static",
  },
  displayMode: {
    value: "contain",
    source: "static",
  },
  navigationDots: {
    value: "",
    source: "static",
  },
}));

const windowWidth = ref(
  typeof window !== "undefined" ? window.innerWidth : 1024
);

const activeAccordion = ref<string | null>(null);

const toggleAccordion = (id: string) => {
  activeAccordion.value = activeAccordion.value === id ? null : id;
};

const diyBoxesSlider = ref(null);

const diyBoxesSwiperSlider = useSwiper(diyBoxesSlider, {
  effect: "slide",
  loop: true,
  grabCursor: true,
  slidesPerView: 1,
  autoplay: true,
  breakpoints: {
    640: {
      slidesPerView: 1,
    },
    768: {
      slidesPerView: 1,
    },
    1024: {
      slidesPerView: 2,
    },
  },
});

const isSliderReady = ref(false);
onMounted(() => {
  // loadProducts();
  setupDefaultQuantities();
  isSliderReady.value = true;

  // Add window resize listener
  if (typeof window !== "undefined") {
    window.addEventListener("resize", () => {
      windowWidth.value = window.innerWidth;
    });
  }
});

onUnmounted(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("resize", () => {
      windowWidth.value = window.innerWidth;
    });
  }
});

const isVideo = computed(() => {
  const fileExtension = guide_main_image?.value?.fileExtension?.toLowerCase();
  return ["mp4", "webm", "ogg"].includes(fileExtension);
});
</script>

<template>
  <section
    class="prodSec mb-10 md:mb-20 lg:mb-24 xl:mb-40 md:mt-2 lg:mt-16 xl:mt-24"
  >
    <div class="container mb-[6rem]">
      <div class="flex flex-wrap">
        <div class="w-full lg:w-[45%] xl:w-1/2 p-0 xl:pe-7">
          <div>
            <div class="relative mb-12 hidden md:block">
              <figure
                class="img-cover aspect-square rounded-3xl overflow-hidden"
              >
                <template v-if="isVideo">
                  <video
                    class="w-full h-full object-cover"
                    controls
                    autoplay
                    loop
                    muted
                  >
                    <source
                      :src="guide_main_image?.value?.url"
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                </template>

                <template v-else>
                  <NuxtImg
                    format="webp"
                    loading="lazy"
                    :src="guide_main_image?.value?.url"
                    :alt="
                      guide_main_image?.value?.alt ||
                      guide_main_image?.value?.name
                    "
                    :width="guide_main_image?.value?.metaData?.width"
                    :height="guide_main_image?.value?.metaData?.height"
                  />
                </template>
              </figure>
            </div>
            <div class="mb-12 hidden md:block">
              <a
                :href="guide_upload?.value?.url"
                download
                target="_blank"
                class="btn-primary hover:bg-cust_green hover:text-white w-full font-redit_sans_con h-12 gap-4 font-normal"
              >
                {{ guid_download_text?.value || $t("plubel.detail.download") }}
                <img
                  src="assets/images/download.svg"
                  class="inline-block"
                  alt=""
              /></a>
            </div>
          </div>
          <div
            class="flex flex-wrap -mx-[15px] md:-mx-2 prod_Slider gap-y-12 mobile:p-3"
          >
            <template v-if="windowWidth < 768">
              <template v-if="isSliderReady">
                <div
                  v-if="diy_box?.value.length"
                  @click="diyBoxesSwiperSlider.prev()"
                  class="swiper-cust-button-prev bg-cust_cream text-cust_gray03 -mt-[4rem]"
                >
                  <i class="fa-solid fa-arrow-left"></i>
                </div>
                <swiper-container
                  ref="diyBoxesSlider"
                  class="swiper-container w-full"
                >
                  <swiper-slide
                    v-for="(box, index) in diy_box?.value"
                    :key="index"
                  >
                    <LazyProductDetailDiyBox :box="box" />
                  </swiper-slide>
                </swiper-container>
                <div
                  v-if="diy_box?.value.length"
                  @click="diyBoxesSwiperSlider.next()"
                  class="swiper-cust-button-next bg-cust_cream text-cust_gray03 -mt-[4rem]"
                >
                  <i class="fa-solid fa-arrow-right"></i>
                </div>
              </template>
              <div v-else class="flex">
                <div
                  v-for="index in 1"
                  :key="index"
                  data-testid="loading"
                  class="w-full md:w-1/2 xl:w-1/2 px-2"
                >
                  <ProductCardSkeleton class="w-full mb-8 px-1 sm:mr-8 mb-8" />
                </div>
              </div>
            </template>
            <template
              v-else
              v-for="(box, index) in diy_box?.value"
              :key="index"
            >
              <LazyProductDetailDiyBox :box="box" />
            </template>
          </div>
        </div>
        <div
          class="w-full lg:w-[55%] xl:w-1/2 font-redit_sans font-light lg:ps-6 xl:ps-14"
        >
          <h6 class="mb-4">{{ config.smal_top_headline?.value }}</h6>
          <h1 class="inner-h1 mb-2">{{ guide_headline?.value }}</h1>
          <p>{{ config.guide_text?.value }}</p>
          <div class="text-center flex flex-wrap pt-8 md:pt-12 gap-y-6">
            <div class="w-full sm:w-1/2 flex justify-center">
              <div class="w-1/3">
                <span class="inline-block mb-1 md:mb-2 text-[0]">
                  <img
                    :src="
                      icon_time?.value?.url || 'assets/images/lieferung.svg'
                    "
                    src="assets/images/lieferung.svg"
                    alt="time"
                  />
                </span>
                <p class="text-lg">{{ timeinfo?.value }}</p>
              </div>
              <div class="w-1/3">
                <span class="inline-block mb-1 md:mb-2 text-[0]">
                  <img
                    :src="
                      icon_implementation?.value?.url ||
                      'assets/images/schwierigkeit.svg'
                    "
                    alt="difficulty"
                  />
                </span>
                <p class="text-lg">
                  {{ how_difficult.value }}
                </p>
              </div>
            </div>
            <div class="w-full sm:w-1/2 flex justify-center">
              <div class="w-1/3 hover:cursor-pointer" @click="handlePrint">
                <span class="inline-block mb-1 md:mb-2 text-[0]">
                  <img src="assets/images/print.svg" alt="" />
                </span>
                <p class="text-lg">{{ $t("plubel.detail.print") }}</p>
              </div>
              <!-- <div class="w-1/3 hover:cursor-pointer">
                <span class="inline-block mb-1 md:mb-2 text-[0]">
                  <img src="assets/images/heart.svg" alt="" />
                </span>
                <p class="text-lg">{{ $t("plubel.detail.like") }}</p>
              </div> -->
              <div class="w-1/3 hover:cursor-pointer" @click="shareViaAPI">
                <span class="inline-block mb-1 md:mb-2 text-[0]">
                  <img src="assets/images/share.svg" alt="" />
                </span>
                <p class="text-lg">{{ $t("plubel.detail.share") }}</p>
              </div>
            </div>
          </div>
          <div class="pt-10 md:pt-14 flex flex-col gap-4">
            <h4 class="mb-0 lg:mb-5 text-28px">
              {{ guide_product_section?.value?.product_project_headline }}
            </h4>
            <template
              v-for="(product, index) in product_project.value"
              :key="index"
            >
              <div
                v-if="product?.project_product_link"
                :key="index"
                class="flex-wrap md:flex-nowrap flex gap-y-2 md:gap-y-0 gap-[10px] md:gap-4 bg-cust_cream py-4 md:py-2 ps-[6px] pe-[10px] md:px-6 rounded-xl md:rounded-2xl lg:rounded-3xl items-start"
              >
                <input
                  type="number"
                  class="w-8 md:w-10 h-8 md:h-10 font-redit_sans bg-white items-center justify-center flex md:mt-4 rounded border border-solid border-cust_black text-center"
                  :min="product.project_product_link.minPurchase || 1"
                  :max="product.project_product_link.calculatedMaxPurchase"
                  :step="product.project_product_link.purchaseSteps || 1"
                  v-model="product.project_product_link.selectedQuantity"
                  @input="
                    updateQuantity(
                      product.project_product_link,
                      $event.target.value
                    )
                  "
                />

                <div class="w-[72px] md:w-[112px] aspect-square">
                  <NuxtImg
                    class="rounded-md"
                    format="webp"
                    loading="lazy"
                    :src="product.project_product_link.cover?.media.url"
                    :alt="
                      product.project_product_link.cover?.media.alt ||
                      product.project_product_link.cover?.media?.name
                    "
                    :width="
                      product.project_product_link.cover?.media.metaData?.width
                    "
                    :height="
                      product.project_product_link.cover?.media.metaData?.height
                    "
                  />
                </div>

                <div
                  class="w-[calc(100%-124px)] md:w-[calc(100%-305px)] md:pt-3"
                >
                  <h5 class="mb-1 font-redit_sans_con font-extrabold">
                    {{ product.project_product_link.translated.name }}
                    <template v-if="product.project_product_link.parentId">
                      - {{ product.project_product_link.options?.[0]?.name }}
                    </template>
                  </h5>
                  <div
                    class="text-sm md:text-base lg:text-xl line-clamp"
                    v-html="product.project_product_link.translated.description"
                  ></div>
                  <small
                    class="text-sm md:hidden text-red-600"
                    v-if="checkCartEnabled(product.project_product_link)"
                    >{{ $t("plubel.detail.notAvailable") }}</small
                  >
                </div>
                <div
                  class="w-[calc(100%-124px)] m-0 md:m-0 md:w-[104px] text-end flex md:flex-col md:pt-4 justify-between items-center md:items-end"
                  style="margin: 0 0 0 auto"
                >
                  <span
                    @click="selectItem(product.project_product_link)"
                    class="w-10 h-10 items-center justify-center inline-flex rounded text-cust_green bg-white md:mb-5 hover:cursor-pointer"
                    :class="{
                      'pointer-events-none border border-red-600':
                        checkCartEnabled(product.project_product_link),
                    }"
                  >
                    <i
                      class="fa-solid fa-check font-bold"
                      v-if="isProductSelected(product.project_product_link.id)"
                    ></i>

                    <i
                      class="fa-solid fa-times"
                      v-if="
                        checkCartEnabled(product.project_product_link) &&
                        !isProductSelected(product.project_product_link.id)
                      "
                    ></i>
                  </span>
                  <small
                    class="text-sm hidden md:block text-red-600"
                    v-if="checkCartEnabled(product.project_product_link)"
                    >{{ $t("plubel.detail.notAvailable") }}</small
                  >
                  <h5
                    class="font-redit_sans_con font-extrabold -order-1 md:order-1 tracking-[0.22px] text-sm"
                  >
                    <SwListingProductPrice
                      :product="product.project_product_link"
                      data-testid="product-box-product-price"
                      class-name="text-lg md:text-xl"
                    />
                  </h5>
                </div>
              </div>
            </template>
          </div>

          <div
            class="flex flex-wrap items-center gap-x-6 mb-8 xl:mb-12 mt-10 xl:mt-14 justify-between gap-5"
          >
            <div>
              <p>{{ $t("plubel.detail.totalPrice") }}</p>
              <p class="text-[15px]">{{ $t("product.pricesIncl") }}</p>
            </div>
            <h3 class="mb-0">
              <SharedPrice
                :value="totalPrice"
                data-testid="order-item-promotion-totalprice"
              >
                <template #afterPrice>*</template>
              </SharedPrice>
            </h3>
          </div>
          <div>
            <div class="flex my-6 justify-between">
              <button
                @click="addToCart"
                :disabled="
                  selectedProducts.length === 0 || isAddToCartProcessing
                "
                class="w-full rounded-[40px] btn-primary h-14 md:h-16 font-redit_sans_con font-medium text-xl md:text-28px"
                :class="{
                  'opacity-50 cursor-not-allowed':
                    selectedProducts.length === 0,
                }"
              >
                {{ translations.product.addToCart }}
                <i
                  v-if="isAddToCartProcessing"
                  class="fa-solid fa-spinner animate-spin text-xl ml-1"
                ></i>
              </button>
            </div>
            <ul
              class="flex flex-wrap gap-y-1 gap-x-4 md:gap-3 lg:gap-6 justify-center md:justify-start prodList"
            >
              <li
                class="bg-[url(assets/images/umweltfreundlich.svg)] bg-no-repeat bg-left ps-7 md:ps-10 text-lg font-redit_sans_con"
              >
                {{ $t("plubel.detail.friendly") }}
              </li>
              <li
                class="bg-[url(assets/images/lieferung.svg)] bg-no-repeat bg-left ps-7 md:ps-10 text-lg font-redit_sans_con"
              >
                {{ $t("plubel.detail.swisOnline") }}
              </li>
              <li
                class="bg-[url(assets/images/heart.svg)] bg-no-repeat bg-left ps-7 md:ps-10 text-lg font-redit_sans_con"
              >
                {{ $t("plubel.detail.fastShippiing") }}
              </li>
            </ul>
          </div>

          <div class="mt-10 md:mt-12 w-full">
            <div class="overflow-x-scroll hide-scrollbar" style="width: 100%">
              <ul
                class="flex flex-nowrap gap-4 mb-6 whitespace-nowrap min-w-max pb-2"
              >
                <li
                  v-for="paymentMethod in paymentMethods"
                  :key="paymentMethod?.id"
                  class="inline-block"
                >
                  <img
                    loading="lazy"
                    class="h-8 w-auto"
                    :src="paymentMethod?.media?.url"
                    :alt="paymentMethod?.media?.alt || paymentMethod?.name"
                    :width="paymentMethod?.media?.metaData?.width"
                    :height="paymentMethod?.media?.metaData?.height"
                  />
                </li>
              </ul>
            </div>
          </div>
          <div class="md:hidden prodAccrod w-full">
            <div
              class="accordian-wrapper w-full bg-cust_cream py-4 px-2 rounded-3xl"
            >
              <div
                v-for="(box, index) in diy_box?.value"
                :key="index"
                class="accordian-item py-4"
              >
                <div
                  class="accordian-head"
                  :class="{ active: activeAccordion === box.id }"
                  @click="toggleAccordion(box.id)"
                >
                  <h5
                    class="normal-case font-bold text-lg md:text-xl after:font-font-awesome leading-6 flex items-start justify-between cursor-pointer font-redit_sans_con"
                  >
                    {{ box.diy_box_headline }}
                  </h5>
                </div>
                <div
                  class="accordian-details leading-6 pt-3 text-base md:text-lg"
                  :class="{ hidden: activeAccordion != box.id }"
                >
                  <p>
                    {{ box.diy_box_text }}
                  </p>
                  <p class="mt-4" v-if="box.diy_box_tip">
                    {{ box.diy_box_tip }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.line-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Max 2 lines */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
